import { supabase } from '../lib/supabase';
export { loadAuditAccounts as loadPerformanceAccounts } from './pinterestAuditService';
export { METRICS, LABELS, DIAGNOSES, metricsFromRaw, diagnose, summarize, change, periodFor, previousPeriod, validPeriod } from '../../supabase/functions/_shared/pinterest-performance.js';

export function waitForAnalytics(ms, signal) {
  return new Promise((resolve, reject) => {
    const cancel = () => { clearTimeout(timer); signal?.removeEventListener('abort', cancel); reject(new DOMException('Refresh stopped', 'AbortError')); };
    const timer = setTimeout(() => { signal?.removeEventListener('abort', cancel); resolve(); }, ms);
    signal?.addEventListener('abort', cancel, { once: true });
    if (signal?.aborted) cancel();
  });
}
async function requestAnalytics(body, signal, endpoint = 'pinterest-performance') {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Sign in again.');
  const base = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
  const controller = new AbortController();
  const abort = () => controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  if (signal?.aborted) abort();
  const timer = setTimeout(abort, 35000);
  try {
    const response = await fetch(`${base}/${endpoint}`, { method: 'POST', signal: controller.signal,
      headers: { Authorization: `Bearer ${session.access_token}`, apikey: import.meta.env.VITE_SUPABASE_ANON_KEY, 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const data = await response.json();
    if (!response.ok) throw Object.assign(new Error(data.error || 'Analytics request failed.'), { status: response.status, retryAfterMs: data.retryAfterMs });
    return data;
  } finally { clearTimeout(timer); signal?.removeEventListener('abort', abort); }
}
export async function loadPerformance(userId, profileId, accountId, period, signal) {
  const records = [];
  for (let offset = 0; ; offset += 500) {
    const { data, error } = await supabase.from('pinterest_performance_periods').select('*')
      .eq('user_id', userId).eq('profile_id', profileId).eq('pinterest_account_id', accountId)
      .eq('period_start', period.start).eq('period_end', period.end).order('pin_id').range(offset, offset + 499).abortSignal(signal);
    if (error) throw error;
    records.push(...data);
    if (data.length < 500) return records;
  }
}
export async function refreshPerformance({ profileId, accountId, current, previous, signal, onProgress, onPins, onRecord, onFailure, request = requestAnalytics, wait = waitForAnalytics }) {
  let nextRequestAt = 0;
  const call = async body => {
    for (let retry = 0; ; retry++) {
      await wait(Math.max(0, nextRequestAt - Date.now()), signal);
      try {
        const data = await request({ ...body, profileId, accountId }, signal);
        nextRequestAt = Date.now() + Math.max(1100, data.delayMs || 0);
        return data;
      } catch (error) {
        if (signal.aborted) throw error;
        if (error.status === 429 || error.status >= 500) {
          nextRequestAt = Date.now() + Math.max(error.status === 429 ? 60000 : 5000 * 2 ** retry, error.retryAfterMs || 0);
          if (retry < 3) { onProgress(`Pinterest ${error.status === 429 ? 'rate limit' : 'temporary failure'}; retry ${retry + 1}/3 after ${new Date(nextRequestAt).toLocaleTimeString()}.`); continue; }
        }
        throw error;
      }
    }
  };
  const pins = new Map(), bookmarks = new Set();
  let bookmark = null;
  do {
    onProgress(`Fetching Pins… ${pins.size} found`);
    const data = await call({ action: 'pins', bookmark });
    data.pins.forEach(pin => pins.set(pin.pin_id, pin));
    bookmark = data.bookmark;
    if (bookmark && bookmarks.has(bookmark)) throw new Error('Pinterest repeated a pagination bookmark. Refresh stopped; previous data was retained.');
    if (bookmark) bookmarks.add(bookmark);
  } while (bookmark);
  onPins([...pins.values()]);
  const failures = new Set();
  let succeeded = 0, failed = 0;
  for (const [name, period] of [['current', current], ['previous', previous]]) {
    if (!period) continue;
    let done = 0;
    for (const pin of pins.values()) {
      onProgress(`Fetching ${name === 'previous' ? 'comparison' : 'current'} analytics… ${done} / ${pins.size} Pins`);
      try {
        const data = await call({ action: 'analytics', period, pin });
        if (signal.aborted) throw new DOMException('Refresh stopped', 'AbortError');
        onRecord(name, data.record); succeeded++;
      } catch (error) {
        if (signal.aborted) throw error;
        failures.add(pin.pin_id); failed++;
        onFailure({ pinId: pin.pin_id, period: name, message: error.message });
        // Account-wide unavailable capabilities cannot be repaired by trying every Pin.
        if (error.status === 401 || /restricted feature/i.test(error.message)) throw error;
      }
      done++;
    }
  }
  onProgress('Calculating performance…');
  return { total: pins.size, successfulPins: pins.size - failures.size, failedPins: failures.size, succeeded, failed };
}

// New durable path. Legacy refresh remains available to already-open clients.
export async function backgroundPerformance({ userId, profileId, accountId, current, previous, signal, start = false, onProgress, onPins, onRecords, onFailure, onWatching }) {
  const context = { profileId, accountId, current, previous };
  let data = await requestAnalytics({ ...context, action: start ? 'start' : 'status' }, signal, 'pinterest-performance-jobs');
  if (!data.run) return null;
  const seenFailures = new Set();
  const watermarks = { current: '1970-01-01T00:00:00.000Z', previous: '1970-01-01T00:00:00.000Z' };
  let pinsLoaded = false;
  while (!signal.aborted) {
    const active = ['queued','running','waiting'].includes(data.run.status);
    onWatching(active);
    const describe = name => `${name === 'current' ? 'Current' : 'Previous'}: ${data.periods[name].done} / ${data.periods[name].total} · ${data.periods[name].state}${Number(data.periods[name].failed) ? ` · ${data.periods[name].failed} failed` : ''}`;
    onProgress(`${describe('current')}${previous ? ` | ${describe('previous')}` : ' | Previous: unavailable'}${data.run.status === 'waiting' ? ` · Next worker attempt ${new Date(data.run.next_attempt_at).toLocaleTimeString()}` : ''}${data.run.mode === 'individual' ? ' · Individual requests (bulk access unavailable/unverified)' : data.run.mode === 'batch' ? ' · Batch requests' : ''}${data.run.error_message ? ` · ${data.run.error_message}` : ''}`);
    for (const failure of data.failures) {
      const key = `${failure.period}:${failure.pin_id}:${failure.error_message}`;
      if (!seenFailures.has(key)) { seenFailures.add(key); onFailure({ pinId: failure.pin_id, period: failure.period, message: failure.error_message }); }
    }
    for (const [name, period] of [['current',current],['previous',previous]]) {
      if (!period) continue;
      let latest = watermarks[name];
      for (let offset = 0;; offset += 500) {
        const { data: records, error } = await supabase.from('pinterest_performance_periods').select('*')
          .eq('user_id',userId).eq('profile_id',profileId).eq('pinterest_account_id',accountId).eq('period_start',period.start).eq('period_end',period.end)
          .gte('synced_at',watermarks[name]).order('synced_at').order('pin_id').range(offset,offset+499).abortSignal(signal);
        if (error) throw error;
        if (records.length) onRecords(name, records);
        for (const record of records) { if (record.synced_at > latest) latest = record.synced_at; }
        if (records.length < 500) break;
      }
      watermarks[name] = latest;
    }
    if (data.run.pins_complete && !pinsLoaded) {
      const pins = [];
      for (let offset = 0;; offset += 500) {
        const {data: page,error} = await supabase.from('pinterest_performance_tasks').select('pin_id,pin_title,destination_url').eq('run_id',data.run.id).eq('period','current').order('pin_id').range(offset,offset+499).abortSignal(signal);
        if (error) throw error; pins.push(...page); if (page.length < 500) break;
      }
      onPins(pins); pinsLoaded = true;
    }
    if (!active) return { background: true, status: data.run.status, total: data.periods.current.total, succeeded: Number(data.periods.current.done)+Number(data.periods.previous.done), failed: Number(data.periods.current.failed)+Number(data.periods.previous.failed) };
    await waitForAnalytics(4000,signal);
    data = await requestAnalytics({...context,action:'status'},signal,'pinterest-performance-jobs');
    if (!data.run) throw new Error('Saved analytics run is unavailable.');
  }
}
